import type { TaxonomyTerm } from '../../model/product/taxonomy';
// Sourced from model/, not from @skin-seoul/shared-components. The previous
// relative import reached across a package boundary into a .tsx tree, which
// inverted the layering (shared-components depends on service, not the other
// way round) and pulled React's type identity into every consumer.
import type { SelectOption } from '../../model/review/interfaces';

export interface TaxonomyResponse {
  skinType: TaxonomyTerm[];
  skinConcern: TaxonomyTerm[];
}

/**
 * Fixes malformed skin data arrays that come as JSON strings
 * Handles: ["[\"Sensitive\"]"] → ["Sensitive"]
 */
export const cleanSkinData = (data: string[]): string[] => {
  if (!Array.isArray(data)) {
    return [];
  }

  return data
    .map((item) => {
      // Check if item is a JSON string like "[\"Sensitive\"]"
      if (typeof item === 'string' && item.startsWith('[') && item.endsWith(']')) {
        try {
          return JSON.parse(item);
        } catch {
          return item;
        }
      }
      return item;
    })
    .flat(); // Flatten in case JSON.parse returns an array
};

// Transform taxonomy data to select options
export const transformSkinTypeOptions = (skinTypes: TaxonomyTerm[] = []): SelectOption[] => {
  return skinTypes.map((item) => ({
    label: item.name.split(' ')?.at(0) || '', // @TODO: API has problem - remove this workaround when fixed
    value: item.name.split(' ')?.at(0) || '',
  }));
};

export const transformSkinConcernOptions = (skinConcerns: TaxonomyTerm[] = []): SelectOption[] => {
  return skinConcerns.map((item) => ({
    label: item.name,
    value: item.name,
  }));
};

// Transform both taxonomy types at once
export const transformTaxonomyData = (taxonomy: TaxonomyResponse | undefined) => {
  if (!taxonomy) {
    return {
      skinTypeOptions: [],
      skinConcernOptions: [],
    };
  }

  return {
    skinTypeOptions: transformSkinTypeOptions(taxonomy.skinType),
    skinConcernOptions: transformSkinConcernOptions(taxonomy.skinConcern),
  };
};
